// src/App.tsx

import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import LoginForm from "./components/LoginForm";
import RegisterPage from "./components/RegisterPage";
import AppLayout from "./layouts/AppLayout";
import { useAuth } from "./context/AuthContext";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import ForgotPasswordPage from "./components/ForgotPasswordPage";
import ResetPasswordPage from "./components/ResetPasswordPage";

import RegisterCompany from "./RegisterCompany";
import CompanyAdminLogin from "./components/CompanyAdminLogin";
import CompanyAddUser from "./CompanyAddUser";
import SuperAdminCompanies from "./components/SuperAdminCompanies";
import { UIProvider } from "./context/UIContext";

import GanttStandalone from "./components/gantt/GanttStandalone";
import MarkwiseProgressReport from "./components/WorksReport/MarkwiseProgressReport";

// =========================================================
// QUERY CLIENT
// =========================================================

const queryClient = new QueryClient();


// =========================================================
// APP
// =========================================================

export default function App() {

  const { token, initialized } = useAuth();

  // -------------------------------------------------------
  // WAIT FOR AUTH INITIALIZATION
  // -------------------------------------------------------

  if (!initialized) {
    return null;
  }


  // -------------------------------------------------------
  // ROUTES
  // -------------------------------------------------------

  return (
    <QueryClientProvider client={queryClient}>

      <Routes>

        {/* =================================================
            MAIN APPLICATION
        ================================================= */}

        <Route
          path="/"
          element={
            token
              ? <AppLayout />
              : <Navigate to="/login" replace />
          }
        />


        {/* =================================================
            LOGIN
        ================================================= */}

        <Route
          path="/login"
          element={<LoginForm />}
        />


        {/* =================================================
            REGISTER
        ================================================= */}

        <Route
          path="/register"
          element={<RegisterPage />}
        />


        {/* =================================================
            FORGOT / RESET PASSWORD
        ================================================= */}

        <Route
          path="/forgot-password"
          element={<ForgotPasswordPage />}
        />

        <Route
          path="/reset-password"
          element={<ResetPasswordPage />}
        />


        {/* =================================================
            REGISTER COMPANY
        ================================================= */}

        <Route
          path="/register-company"
          element={<RegisterCompany />}
        />


        {/* =================================================
            COMPANY ADMIN LOGIN
        ================================================= */}

        <Route
          path="/company-admin-login"
          element={<CompanyAdminLogin />}
        />


        {/* =================================================
            SUPER ADMIN COMPANY SELECTION
        ================================================= */}

        <Route
          path="/super-admin"
          element={
            token
              ? <SuperAdminCompanies />
              : <Navigate to="/login" replace />
          }
        />


        {/* =================================================
            COMPANY ADMIN / USER MANAGEMENT
        ================================================= */}

        <Route
          path="/add-user"
          element={
            <UIProvider>
              <CompanyAddUser />
            </UIProvider>
          }
        />


        {/* =================================================
            GANTT
        ================================================= */}

        <Route
          path="/gantt"
          element={<GanttStandalone />}
        />

        <Route
          path="/gantt-print"
          element={<GanttStandalone />}
        />


        {/* =================================================
            MARKWISE PROGRESS
        ================================================= */}

        <Route
          path="/markwise-progress"
          element={<MarkwiseProgressReport />}
        />


        {/* =================================================
            CATCH-ALL
        ================================================= */}

        <Route
          path="*"
          element={
            <Navigate
              to={token ? "/" : "/login"}
              replace
            />
          }
        />

      </Routes>

    </QueryClientProvider>
  );
}